/* eslint-disable jsx-a11y/anchor-is-valid */
import MaterialReactTable, { type MRT_ColumnDef } from 'material-react-table'
import React, { useEffect, useRef, useState, useMemo, useCallback} from 'react'
import { Link } from 'react-router-dom'
import {KTSVG as KtSvg} from '../../_metronic/helpers'
import { ITableModel } from '../models/orders/table.model'
import CheckboxItem from './checkbox/checkbox'
import { FormControlLabel, Radio, RadioGroup } from '@mui/material'
import { submitPayments, filterOrdersTable, getStates } from '../services/order.service'
import { UTCNoTime } from '../utils/global.utils'
import Loading from './loading.component'

type Props = {
  className?: string,
  tableState: ITableModel,
  setSelectedRows?: any,
  selectedRows?:any
  addedPayments?: any[]
  setAddedPayments?: (payments: any[]) => void
  results?: any[],
  columns?: MRT_ColumnDef<any>[]
  isTableLoading?: boolean
  compactLayout?: boolean
}

type FilterOptions = {
  status: string
  transaction_searchType: string
  transaction_searchCompanyId: string | number
  transaction_searchCompanyName: string
  transaction_state: string
  tgOrder_propertyCounty: string
  transaction_rush: string
  transaction_currentDueDate: string
}

const createFilterOptions = (status: string, results?: any[]): FilterOptions => ({
  status: status || '',
  transaction_searchType: '',
  transaction_searchCompanyId: '',
  transaction_searchCompanyName: '',
  transaction_state: '',
  tgOrder_propertyCounty: '',
  transaction_rush: '',
  transaction_currentDueDate: '',
})

const getPrimaryValue = (value?: string) => value?.split(',')[0].trim() ?? ''

const getDerivedOptions = (sourceRows: any[] = []) => {
  const statesMap = new Map<string, string>()
  const assignedSet = new Set<string>()
  const productsSet = new Set<string>()
  const rushSet = new Set<any>(['File Number', 'Property', 'Town', 'Order Date', 'Due Date', 'Notes', 'Assigned To', 'Status', 'Source'])

  sourceRows.forEach((row: any) => {
    if (row?.state && row?.stateName) {
      statesMap.set(row.state, row.stateName)
    }

    if (row?.assignedTo?.name) {
      assignedSet.add(row.assignedTo.name)
    }

    const productName = getPrimaryValue(row?.product)
    if (productName) {
      productsSet.add(productName)
    }

    const rushName = getPrimaryValue(row?.rushPriority)
    if (rushName) {
      rushSet.add(rushName)
    }
  })

  return {
    states: Array.from(statesMap.entries()).map(([state, stateName]) => ({
      state,
      stateName,
    })),
    assigned: Array.from(assignedSet),
    product: Array.from(productsSet),
    rushPriority: Array.from(rushSet),
  }
}

const mapFilteredRow = (row: any, index: number, status?: string) => ({
  id: index + 1,
  transactionId: row.transaction_id ?? 0,
  orderID: row.tgOrder_tgOrderId ?? 0,
  fileNumber: row.transaction_agencyOrderId ?? 0,
  property: row.transaction_address ?? '',
  county: row.transaction_county ?? '',
  state: row.transaction_state ?? '',
  city: row.transaction_city ?? '',
  status: row.status,
  tab: status,
  paid: Boolean(row.transaction_paid),
  oldPaid: Boolean(row.transaction_paid),
  product: `${row.transaction_searchType}, ${row.transactionType_type}`,
  orderDate: row.transaction_transactionDateTime ? UTCNoTime(row.transaction_transactionDateTime) : null,
  deliveredDate: row.transaction_completeMovementDate ? UTCNoTime(row.transaction_completeMovementDate) : null,
  closingDate: row.transaction_currentDueDate ? UTCNoTime(row.transaction_currentDueDate) : null,
  assignedTo: { name: row.transaction_searchCompanyName, id: row.transaction_searchCompanyId },
  notifications: row.transaction_comments,
  source: row.source ?? '',
  rush: row.rushPriority,
  rushAction: row.rushAction ?? '',
  reportingOrgId: row.transaction_reportingOrgId ?? '',
  rushPriority: row.transaction_rush ?? '',
  transaction_searchType: row.transaction_searchType ?? '',
  transactionType_type: row.transactionType_type ?? '',
  transaction_currentProcessingQueue: row.transaction_currentProcessingQueue ?? '',
  transaction_status: row.transaction_status ?? '',
})

const DEFAULT_COLUMN_MIN_SIZE = 80

const getColumnMinWidth = (column: Partial<MRT_ColumnDef<any>> & { minWidth?: number }) => {
  if (typeof column?.minSize === 'number') {
    return column.minSize
  }

  if (typeof column?.size === 'number') {
    return column.size
  }

  if (typeof column?.minWidth === 'number') {
    return column.minWidth
  }

  return DEFAULT_COLUMN_MIN_SIZE
}

const getColumnVisibilityKey = (column: Partial<MRT_ColumnDef<any>>) => {
  if (typeof column?.id === 'string' && column.id.length > 0) {
    return column.id
  }

  if (typeof column?.accessorKey === 'string' && column.accessorKey.length > 0) {
    return column.accessorKey
  }

  return null
}

const toMrtColumn = (column: any): MRT_ColumnDef<any> => {
  if (column?.accessorKey || column?.accessorFn) {
    return column as MRT_ColumnDef<any>
  }

  const field = column?.field ?? ''

  return {
    id: field,
    accessorFn: (row: any) => row?.[field],
    header: column?.headerName ?? field,
    size: column?.minWidth,
    minSize: getColumnMinWidth(column),
    Cell: column?.renderCell
      ? ({ row, cell }) =>
          column.renderCell({
            row: row.original,
            value: cell.getValue(),
            field,
          })
      : undefined,
  }
}

const OrderTable: React.FC<Props> = ({className, tableState, selectedRows , setSelectedRows, addedPayments, setAddedPayments, results, columns: columnsProp, isTableLoading = false, compactLayout = false}) => {
  const filterRef = useRef<HTMLInputElement>(null)
  const [rows, setRows] = useState<any[]>([])
  const [loadingPayments, setLoadingPayments] = useState(false)
  const [getState, setGetState] = useState<any[]>([])
  const [county, setCounty] = useState<any[]>([])
  const [localLoading, setLocalLoading] = useState(false)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 100 })
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({})
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(() => createFilterOptions(tableState?.status, results))
  const [selectedRush, setSelectedRush] = useState('')
  const isStaticSortRef = useRef(false)
  const staticSortFieldRef = useRef<string | null>(null)

  const RUSH_SORT_OPTIONS = [
    'File Number',
    'Property',
    'Town',
    'Order Date',
    'Due Date',
    'Notes',
    'Assigned To',
    'Status',
    'Source',
  ]
  const STATIC_RUSH_SORT_MAP: Record<string, string> = {
    'File Number': 'fileNumber',
    'Property': 'property',
    'Town': 'city',
    'Order Date': 'orderDate',
    'Due Date': 'closingDate',
    'Notes': 'notifications',
    'Assigned To': 'assignedTo.name',
    'Status': 'status',
    'Source': 'source',
  }

  const shouldCallFilterApi = (filters: FilterOptions) => {
    const { transaction_rush, status, ...rest } = filters;

    if (
      transaction_rush &&
      RUSH_SORT_OPTIONS.includes(transaction_rush)
    ) {
      return false;
    }

    if (
      transaction_rush &&
      !RUSH_SORT_OPTIONS.includes(transaction_rush)
    ) {
      return true;
    }

    return Object.values(rest).some(
      value =>
        value !== '' &&
        value !== null &&
        value !== undefined
    );
  };
  const getValueByPath = (obj: any, path: string) =>
    path.split('.').reduce((acc, key) => acc?.[key], obj)

  const loading = isTableLoading || localLoading
  const tableFontSize = compactLayout ? '12px' : '13px'
  const tableHeadPadding = compactLayout ? '4px 3px' : '6px 4px'
  const tableBodyPadding = compactLayout ? '4px 5px' : '6px 8px'
  const tableLayoutMode = compactLayout ? 'semantic' : 'grid'
  const tableWidth = compactLayout ? '100%' : 'max-content'
  const tableCssLayout = compactLayout ? 'auto' : 'fixed'
  const derivedOptions = useMemo(() => getDerivedOptions(tableState?.rows), [tableState?.rows])  
  const rowsById = useMemo(
    () => new Map(rows.map((row) => [`${row?.id}`, row])),
    [rows]
  )

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => {
      if (columnsProp?.length) {
        return columnsProp
      }

      if (!tableState?.columns?.length) {
        return []
      }

      return tableState.columns.map(toMrtColumn)
    },
    [columnsProp, tableState?.columns]
  )
  const visibleColumns = useMemo(
    () => columns.filter((column) => {
      const visibilityKey = getColumnVisibilityKey(column)

      if (!visibilityKey) {
        return true
      }

      return tableState?.hiddenColumns?.[visibilityKey] !== true
    }),
    [columns, tableState?.hiddenColumns]
  )
  const tableMinWidth = useMemo(
    () => visibleColumns.reduce((total, column) => total + getColumnMinWidth(column as Partial<MRT_ColumnDef<any>>), 0),
    [visibleColumns]
  )
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
const sortRowsLocally = useCallback(
  (fieldPath: string, order: 'asc' | 'desc') => {
    setRows(prev => {
      const sorted = [...prev].sort((a, b) => {
        const valA = getValueByPath(a, fieldPath)
        const valB = getValueByPath(b, fieldPath)

        if (valA == null) return 1
        if (valB == null) return -1

        const dateA = new Date(valA)
        const dateB = new Date(valB)

        const timeA = dateA.getTime()
        const timeB = dateB.getTime()

        if (
          !Number.isNaN(timeA) &&
          !Number.isNaN(timeB)
        ) {
          return order === 'asc' ? timeA - timeB : timeB - timeA
        }

        if (typeof valA === 'string') {
          return order === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA)
        }
        return order === 'asc'
          ? valA - valB
          : valB - valA
      })

      return sorted
    })
  },
  []
)

  useEffect(() => {
    if (!Array.isArray(selectedRows)) {
      setRowSelection({})
      return
    }

    const selectionState = selectedRows.reduce((acc: Record<string, boolean>, rowId: any) => {
      acc[`${rowId}`] = true
      return acc
    }, {})

    setRowSelection(selectionState)
  }, [selectedRows])

useEffect(() => {
  if (isStaticSortRef.current) return

  setRows(tableState?.rows ?? [])
}, [tableState?.rows])

  useEffect(() => {
    const fetchStatesData = async () => {
      try {
        const response = await getStates();
        setGetState(response);
      } catch (error) {
        console.error("Failed to fetch states:", error);
      }
    };

    fetchStatesData();
  }, []);

  useEffect(() => {
    setFilterOptions(createFilterOptions(tableState?.status, results))
  }, [results, tableState?.status]);

  useEffect(() => {
    if (filterRef.current) {
      filterRef.current.value =
        tableState.filterText && tableState.filterText !== '_reset'
          ? `${tableState.filterText}`
          : ''
    }
  }, [tableState.filterText])

  const onFilterHandler = useCallback((resetFilter:boolean) => {
    const newValue = resetFilter ? '_reset' : filterRef.current?.value
    tableState.settings.updatePageState('filterText', newValue)
  }, [tableState.settings])

  const onKeyPressHandler = (e:React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onFilterHandler(false)
  }

  const addPayments = useCallback(() => {
    if (!addedPayments?.length) {
      return
    }

    setLoadingPayments(true)
    submitPayments(addedPayments)
      .then((res: any) => {
        if (res?.success?.msg && setAddedPayments) setAddedPayments([])
      })
      .finally(() => setLoadingPayments(false))
  }, [addedPayments, setAddedPayments])

  const fetchFilteredData = useCallback(async (filters: FilterOptions) => {
    setLocalLoading(true)
    try {
      const response = await filterOrdersTable(filters)
      const apiResults = response?.success?.msg?.results
      if (Array.isArray(apiResults)) {
        setRows(apiResults.map((row: any, index: number) => mapFilteredRow(row, index, tableState?.status)))
      } else {
        setRows([])
      }
    } catch (error) {
      console.error('Failed to filter orders:', error)
    } finally {
      setLocalLoading(false)
    }
  }, [tableState?.status])


  useEffect(() => {
    if (!shouldCallFilterApi(filterOptions)) {
      return
    }

    fetchFilteredData(filterOptions)
  }, [filterOptions, fetchFilteredData])


  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      const { name, value } = e.target
      const key = name as keyof FilterOptions

      if (key === 'transaction_rush') {
        setSelectedRush(value)

        if (STATIC_RUSH_SORT_MAP[value]) {
          isStaticSortRef.current = true
          staticSortFieldRef.current = STATIC_RUSH_SORT_MAP[value]
          setSortOrder('asc')
          sortRowsLocally(STATIC_RUSH_SORT_MAP[value], 'asc')

          return
        }

        setFilterOptions(prev => ({
          ...prev,
          transaction_rush: value,
        }))
        return
      }

      setFilterOptions(prev => {
        const updated = {
          ...prev,
          [key]: value,
        }

        if (key === 'transaction_state') {
          updated.tgOrder_propertyCounty = ''

          const stateObject = getState.find(
            (item: any) => item.stateCode === value
          )

          setCounty(stateObject?.counties || [])
        }

        return updated
      })
    },
    [getState, sortRowsLocally]
  )

  const handleResetFilterChange = useCallback((e: React.SyntheticEvent, type: 'radio' | 'dropdown') => {
    e.preventDefault()
    e.stopPropagation()
    setFilterOptions(prev => {
      const updated = { ...prev }
      if (type === 'radio') {
        updated.transaction_currentDueDate = ''
      } else {
        updated.transaction_searchType = ''
        updated.transaction_searchCompanyName = ''
        updated.transaction_state = ''
        updated.tgOrder_propertyCounty = ''
        updated.transaction_rush = ''
        isStaticSortRef.current = false
        staticSortFieldRef.current = null
        setSelectedRush('')
        setCounty([])
      }
      fetchFilteredData(updated)
      return updated
    })
  }, [fetchFilteredData])

  const handleRowSelectionChange = useCallback((updaterOrValue: any) => {
    const nextSelection = typeof updaterOrValue === 'function'
      ? updaterOrValue(rowSelection)
      : updaterOrValue

    setRowSelection(nextSelection)

    if (setSelectedRows) {
      const ids = Object.keys(nextSelection)
        .filter((key) => nextSelection[key])
        .map((key) => rowsById.get(key)?.id ?? key)

      setSelectedRows(ids)
    }
  }, [rowSelection, rowsById, setSelectedRows])

  const renderTopToolbar = () => (
    <div className='card-header pt-3 pb-3 d-flex flex-column align-items-start w-100'>

      {/* Row 1: Title + search */}
      <div className='d-flex align-items-center w-100 mb-2'>
        <h3 className='card-title mb-0 me-3'>
          <span className='fw-bold fs-3 text-primary'>{tableState.settings.title}</span>
          {tableState.settings.showCount && (
            <span className='fw-semibold rounded card-qty text-white mx-2'>{rows.length}</span>
          )}
        </h3>

        {process.env.REACT_APP_ENV === 'fuckingshit' && (
          <CheckboxItem
            model={{ label: 'Self assign', position: 0, isChecked: false }}
            onChangeHandler={() => {}}
          />
        )}

        {tableState.settings.showButton && (
          <Link to={tableState.settings.buttonLink} className='btn btn-sm btn-primary ms-2 me-3'>
            <i className='fa fa-plus' />
            {tableState.settings.buttonText}
          </Link>
        )}

        <div className='search-box rounded border-gray d-flex flex-row align-items-center ms-auto'>
          <input type='search' ref={filterRef} onKeyPress={onKeyPressHandler} />
          <div onClick={() => onFilterHandler(false)}>
            <KtSvg path='/media/ATR/icons/Icon feather-search.svg' className='svg-icon-5 search-icon' />
          </div>
          <button type='button' className='cursor-pointer text-primary ms-2 p-0' style={{ fontSize: '15px', background: 'none', border: 'none' }} onClick={() => onFilterHandler(true)}>
            Reset
          </button>
        </div>
      </div>

      <div className='d-flex flex-row w-100 mb-2'>
        <RadioGroup
          name='transaction_currentDueDate'
          className='sort-box d-flex flex-row align-items-center'
          value={filterOptions.transaction_currentDueDate}
          onChange={handleFilterChange}
        >
          <FormControlLabel value='late' control={<Radio size='small' />} label='Late' />
          <FormControlLabel value='dueToday' control={<Radio size='small' />} label='Due Today' />
          <FormControlLabel value='dueInNextTwoDays' control={<Radio size='small' />} label='+2' />
          <button
            type='button'
            className='cursor-pointer text-primary ms-2 p-0'
            style={{
              fontSize: '15px',
              background: 'none',
              border: 'none',
              alignSelf: 'center',
              lineHeight: '1',
              marginBottom: "10px"
            }}
            onClick={e => handleResetFilterChange(e, 'radio')}
          >
      Reset
    </button>
        </RadioGroup>
      </div>

      <div className='d-flex flex-row w-100 align-items-center flex-wrap gap-2'>
        <select name='transaction_searchType' className='form-select form-select-sm form-select-solid' style={{ width: '14%', borderRadius: 0 }} value={filterOptions.transaction_searchType} onChange={handleFilterChange}>
          <option value=''>All Products</option>
          {derivedOptions.product.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>

        <select name='transaction_searchCompanyName' className='form-select form-select-sm form-select-solid' style={{ width: '14%', borderRadius: 0 }} value={filterOptions.transaction_searchCompanyName} onChange={handleFilterChange}>
          <option value=''>All Search Companies</option>
          {derivedOptions.assigned.map((item) => <option key={item} value={item}>{item}</option>)}
        </select>

        <select name='transaction_state' className='form-select form-select-sm form-select-solid' style={{ width: '14%', borderRadius: 0 }} value={filterOptions.transaction_state} onChange={handleFilterChange}>
          <option value=''>State</option>
          {derivedOptions.states.map((item) => <option key={item.state} value={item.state}>{item.state}</option>)}
        </select>

        <select name='tgOrder_propertyCounty' className='form-select form-select-sm form-select-solid' style={{ width: '14%', borderRadius: 0 }} value={filterOptions.tgOrder_propertyCounty} onChange={handleFilterChange}>
          {county.length === 0
            ? <option value=''>Select State First</option>
            : <>
                <option value=''>County</option>
                {county.map((item: any) => <option key={item.countyName} value={item.countyName}>{item.countyName}</option>)}
              </>
          }
        </select>

        <select name='transaction_rush' className='form-select form-select-sm form-select-solid' style={{ width: '14%', borderRadius: 0 }} value={selectedRush} onChange={handleFilterChange}>
          <option value="">Rush Priority</option>
          {RUSH_SORT_OPTIONS.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
          {derivedOptions.rushPriority
            .filter(item => !RUSH_SORT_OPTIONS.includes(item))
            .map(item => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
        </select>
        <button
          type="button"
          aria-label="Toggle sort order"
          style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0, marginTop: 0 }}
          onClick={() => {
            if (!isStaticSortRef.current || !staticSortFieldRef.current) return
            const nextOrder = sortOrder === 'asc' ? 'desc' : 'asc'
            setSortOrder(nextOrder)
            sortRowsLocally(staticSortFieldRef.current, nextOrder)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              if (!isStaticSortRef.current || !staticSortFieldRef.current) return
              const nextOrder = sortOrder === 'asc' ? 'desc' : 'asc'
              setSortOrder(nextOrder)
              sortRowsLocally(staticSortFieldRef.current, nextOrder)
            }
          }}
        >
          {sortOrder === 'desc' ? (
            <KtSvg path="/media/ATR/icons/Icon sort-amount-down.svg" className="svg-icon-2" />
          ) : (
            <KtSvg path="/media/ATR/icons/Icon sort-amount-up.svg" className="svg-icon-2" />
          )}
        </button>
        <button type='button' className='cursor-pointer text-primary ms-2 p-0' style={{ fontSize: '15px', background: 'none', border: 'none', marginBottom: '6px' }} onClick={e => handleResetFilterChange(e, 'dropdown')} onKeyDown={e => e.key === 'Enter' && handleResetFilterChange(e, 'dropdown')}>Reset</button>
      </div>

      {/* Save Payments */}
      {!!addedPayments?.length && (
        <div className='mt-2'>
          <button type='button' className='btn btn-primary' onClick={addPayments}>
            {loadingPayments
              ? <span className='indicator-progress' style={{ display: 'block' }}>
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
              : <span>Save Payment</span>
            }
          </button>
        </div>
      )}
    </div>
  )

  return (
    <div className={`card ${className}`}>
      <div className='card-body custmTable-head p-0' style={{ position: 'relative' }}>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              borderRadius: '4px',
            }}
          >
            <Loading />
          </div>
        )}
        <MaterialReactTable
          columns={columns}
          data={rows}
          getRowId={(row, index) => `${row?.id ?? index}`}
          enableRowSelection={Boolean(setSelectedRows)}
          enableMultiRowSelection={Boolean(setSelectedRows)}
          onRowSelectionChange={handleRowSelectionChange}
          enableColumnActions={false}
          enableSorting={false}
          state={{
            isLoading: false,
            showProgressBars: false,
            pagination,
            columnVisibility: tableState.hiddenColumns,
            rowSelection,
          }}
          onPaginationChange={setPagination}
          manualFiltering
          enableColumnResizing
          columnResizeMode='onChange'
          enableFilters={false}
          enableColumnFilters={false}
          enableHiding
          enableDensityToggle={false}
          enableFullScreenToggle
          enableGlobalFilter={false}
          enableTopToolbar={!tableState.settings.hideComponents}
          layoutMode={tableLayoutMode}
          positionToolbarAlertBanner='bottom'
          localization={{ noRecordsToDisplay: 'No records found' }}
          renderTopToolbar={tableState.settings.hideComponents ? undefined : () => renderTopToolbar()}
          muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 570px)', overflowX: 'auto', overflowY: 'auto', scrollbarGutter: 'stable' } }}
          muiTableProps={{
            sx: {
              fontFamily: '"Open Sans", sans-serif',
              fontSize: tableFontSize,
              tableLayout: tableCssLayout,
              minWidth: `${tableMinWidth}px`,
              width: tableWidth,
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 700,
              backgroundColor: '#E8E8F0',
              borderRight: '1px solid #d7d5d5',
              padding: tableHeadPadding,
              fontSize: tableFontSize,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          muiTableBodyCellProps={{
            sx: {
              backgroundColor: '#FDFDFD',
              borderRight: '1px solid #DFDFDF',
              padding: tableBodyPadding,
              fontSize: tableFontSize,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: {
              border: '1px solid #dddddd',
              '& .MuiSkeleton-root': {
                width: '80% !important',
              },
            },
          }}
          muiBottomToolbarProps={{
            sx: { borderTop: '1px solid #DFDFDF' },
          }}
          muiTablePaginationProps={{
            rowsPerPageOptions: [10, 25, 50, 100],
            showFirstButton: true,
            showLastButton: true,
          }}
        />
      </div>
    </div>
  )
}

export {OrderTable}